import { appApi } from "@src/integrations/redux/app-api";
import { HTTP_METHODS } from "@src/utils/network-constants";

// Adjust to match your backend response shape
export interface RideDriverDto {
  rideId: string;
  firstName: string;
  lastName: string;
  profilePic: string;
  from: string;
  toDest: string;
  rideDateTime: string;
  phoneNumber: string;
  userId?: string;
  room?: number;
  text?: string;
  flexMinutes?: number;
  partsCount?: number;
  isMyUserPresent?: string;
  isOwnRide?: boolean;
  seatsLeft?: number;
  verifiedOrg?: string;
  rideType?: string;
  stopName?: string;
  stopLat?: number;
  stopLng?: number;
  visible?: boolean | number;
  departed?: boolean;
  // Add other fields as needed based on the response
  [key: string]: any;
}

export interface RideStop {
  trackStopId: string; stopName: string; lat: number; lng: number; seqNum: number;
  estimatedArrivalTime?: string | null;
}
export interface RideBooking {
  userId: string; firstName: string; lastName: string; status: string; numPeople: number; phoneNumber?: string;
  originName?: string; originLat?: number; originLng?: number;
  destName?: string; destLat?: number; destLng?: number;
  trackStopId?: string; stopName?: string; stopLat?: number; stopLng?: number;
}
export interface RideDetailsDto extends RideDriverDto {
  fromLat: number; fromLng: number; destLat: number; destLng: number;
  stops: RideStop[]; booking: RideBooking | null; passengers: RideBooking[]; autoAccept: boolean;
}
export interface JoinRideRequest {
  spaceId: string; rideId: string; numPeople?: number; trackStopId?: string;
  pickup?: {name: string; lat: number; lng: number};
  dropoff?: {name: string; lat: number; lng: number};
}

export interface GetRidesResponse {
  data: RideDriverDto[];
  hasMore?: boolean;
}

export interface AddRideRequest {
  spaceId: string;
  driving: boolean;
  departureTime: string;
  fromName: string;
  fromLat: number;
  fromLng: number;
  toName: string;
  toLat: number;
  toLng: number;
  room: number;
  notes: string;
  flexMinutes: number;
}

export const ridesApi = appApi.enhanceEndpoints({addTagTypes: ['QuickRides']}).injectEndpoints({
  endpoints: (builder) => ({
    getRidesBySpace: builder.query<
      GetRidesResponse, // Updated return type
      {
        spaceId: string;
        locationName?: string;
        lat?: number;
        lng?: number;
        radius?: number;
      }
    >({
      query: ({ spaceId, locationName, lat, lng, radius }) => ({
        url: `api/v1/rides/${spaceId}`,
        method: HTTP_METHODS.GET,
        params: {
          locationName,
          lat,
          lng,
          radius,
        },
      }),
    }),
    browseRides: builder.query<GetRidesResponse, {spaceId: string; date?: string; lat?: number; lng?: number; radius?: number; view?: string; page?: number}>({
      query: ({spaceId, ...params}) => ({
        url: `/api/v1/rides/${encodeURIComponent(spaceId)}/browse`,
        method: HTTP_METHODS.GET,
        params,
      }),
      providesTags: (_result, _error, {spaceId}) => [{type: 'QuickRides', id: spaceId}],
      keepUnusedDataFor: 60,
    }),
    rideDetails: builder.query<RideDetailsDto, {spaceId: string; rideId: string}>({
      query: ({spaceId, rideId}) => `/api/v1/rides/${encodeURIComponent(spaceId)}/${encodeURIComponent(rideId)}/details`,
      providesTags: (_result, _error, {spaceId}) => [{type: 'QuickRides', id: spaceId}],
      keepUnusedDataFor: 60,
    }),
    reviewPassenger: builder.mutation<{status: string}, {spaceId: string; rideId: string; userId: string; decision: 'accept' | 'reject'}>({
      query: ({spaceId, rideId, userId, decision}) => ({
        url: `/api/v1/rides/${encodeURIComponent(spaceId)}/${encodeURIComponent(rideId)}/passengers/${encodeURIComponent(userId)}`,
        method: HTTP_METHODS.POST, body: {decision},
      }),
      invalidatesTags: (_result, _error, {spaceId}) => [{type: 'QuickRides', id: spaceId}],
    }),
    joinRide: builder.mutation<{status: string}, JoinRideRequest>({
      query: ({spaceId, rideId, ...body}) => ({
        url: `/api/v1/rides/${encodeURIComponent(spaceId)}/${encodeURIComponent(rideId)}/join`,
        method: HTTP_METHODS.POST,
        body,
      }),
      invalidatesTags: (_result, _error, {spaceId}) => [{type: 'QuickRides', id: spaceId}],
    }),
    cancelRide: builder.mutation<void, {spaceId: string; rideId: string}>({
      query: ({spaceId, rideId}) => ({url: `/api/v1/rides/${encodeURIComponent(spaceId)}/${encodeURIComponent(rideId)}/join`, method: 'DELETE', body: {}}),
      invalidatesTags: (_result, _error, {spaceId}) => [{type: 'QuickRides', id: spaceId}],
    }),
    addRide: builder.mutation<{rideId: string}, AddRideRequest>({
      query: ({spaceId, ...body}) => ({
        url: `/api/v1/rides/${encodeURIComponent(spaceId)}`,
        method: HTTP_METHODS.POST,
        body,
      }),
      invalidatesTags: (_result, _error, {spaceId}) => [{type: 'QuickRides', id: spaceId}],
    }),
  }),
  overrideExisting: "throw",
});

export const {
  useGetRidesBySpaceQuery,
  useLazyGetRidesBySpaceQuery,
  useBrowseRidesQuery,
  useRideDetailsQuery,
  useReviewPassengerMutation,
  useJoinRideMutation,
  useCancelRideMutation,
  useAddRideMutation,
} = ridesApi;
